import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePlatform } from "@/context/PlatformContext";
import { JasrMark } from "@/components/JasrLogo";
import { api, formatApiError } from "@/lib/api";
import { Loader2, ArrowLeft, CheckCircle2, Building2, User, Mail, Lock } from "lucide-react";

export default function Register() {
  const { login, refreshMe } = useAuth();
  const { name, logo_base64 } = usePlatform();
  const navigate = useNavigate();

  const LogoIcon = () => logo_base64
    ? <img src={logo_base64} alt={name} className="w-4 h-4 object-contain" />
    : <ArrowLeft className="w-4 h-4 text-white rtl-flip" />;

  const [form, setForm] = useState({ name: "", email: "", company_name: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { setError("كلمتا المرور غير متطابقتين"); return; }
    if (form.password.length < 6) { setError("كلمة المرور 6 أحرف على الأقل"); return; }
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/register", {
        name: form.name,
        email: form.email,
        company_name: form.company_name,
        password: form.password,
      });
      if (data?.token) localStorage.setItem("auth_token", data.token);
      await refreshMe();
      navigate("/dashboard", { replace: true });
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const perks = [
    "مزامنة تلقائية كل 15 دقيقة",
    "استيراد Excel ذكي بالصور",
    "تحليلات لحظية متقدمة",
    "دعم فروع متعددة",
  ];

  return (
    <div className="min-h-screen flex" dir="rtl">
      {/* Visual panel */}
      <div className="hidden lg:flex w-[48%] relative flex-col overflow-hidden"
        style={{ background: "linear-gradient(135deg, #060810 0%, #0A1228 100%)" }}>
        <div className="absolute inset-0 dot-grid opacity-20" />
        <div className="absolute top-1/3 left-1/2 w-72 h-72 bg-[#004EEB]/12 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-[#6366F1]/10 rounded-full blur-3xl" />
        <div className="grain absolute inset-0" />

        <div className="relative z-10 flex flex-col h-full p-12">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-2.5 w-fit">
            <div className="w-8 h-8 bg-[#004EEB] rounded-lg grid place-items-center glow-blue">
              <LogoIcon />
            </div>
            <span className="text-white font-black text-sm tracking-tight" style={{ fontFamily: "'Tajawal', sans-serif" }}>
              {name}<span className="text-[#4d84ff]">.</span>
            </span>
          </Link>

          <div className="flex-1 flex flex-col justify-center space-y-8">
            <div className="fade-up">
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#4d84ff]/70 font-semibold border border-[#004EEB]/25 bg-[#004EEB]/8 px-3 py-1 rounded-full">
                حساب مجاني · لا بطاقة ائتمان
              </span>
              <h1 className="text-4xl font-black text-white leading-tight mt-5 mb-4">
                انضم إلى تجار<br />
                <span className="gradient-text">يوفّرون الوقت</span>
              </h1>
              <p className="text-white/45 text-[15px] leading-relaxed max-w-sm">
                أنشئ حسابك وابدأ في تزامن مخزونك مع سلة خلال دقيقتين.
              </p>
            </div>

            <div className="space-y-3">
              {perks.map((p, i) => (
                <div key={p} className="flex items-center gap-3 text-white/65 text-sm" style={{ animationDelay: `${i * 80}ms` }}>
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  {p}
                </div>
              ))}
            </div>
          </div>

          <p className="text-white/20 text-xs">
            لديك حساب؟{" "}
            <Link to="/login" className="text-[#60A5FA] hover:text-white transition-colors">سجّل الدخول</Link>
          </p>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center bg-[#F8FAFC] p-6 lg:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-6 fade-up">
          {/* Mobile brand */}
          <div className="lg:hidden flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-[#004EEB] rounded-lg grid place-items-center">
                {logo_base64
                  ? <img src={logo_base64} alt={name} className="w-4 h-4 object-contain" />
                  : <JasrMark size={18} />
                }
              </div>
              <span className="font-black text-slate-900" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                {name}<span className="text-[#004EEB]">.</span>
              </span>
            </div>
            <Link to="/login" className="text-sm text-slate-500 hover:text-slate-900">تسجيل الدخول</Link>
          </div>

          <div>
            <p className="text-[11px] tracking-[0.18em] uppercase text-slate-400 font-semibold">إنشاء حساب جديد</p>
            <h2 className="text-3xl font-bold mt-1.5 tracking-tight text-slate-900">ابدأ مجاناً</h2>
            <p className="text-slate-500 mt-2 text-sm">أنشئ حسابك وابدأ رحلة التزامن الذكي.</p>
          </div>

          <div className="space-y-3.5">
            <InputField icon={User} label="الاسم الكامل" type="text" value={form.name}
              onChange={set("name")} placeholder="محمد أحمد" required testid="register-name-input" />
            <InputField icon={Building2} label="اسم الشركة / المتجر" type="text" value={form.company_name}
              onChange={set("company_name")} placeholder="متجر النجوم" testid="register-company-input" />
            <InputField icon={Mail} label="البريد الإلكتروني" type="email" value={form.email}
              onChange={set("email")} placeholder="you@example.com" required testid="register-email-input" />
            <InputField icon={Lock} label="كلمة المرور" type="password" value={form.password}
              onChange={set("password")} placeholder="٦ أحرف على الأقل" required testid="register-password-input" />
            <InputField icon={Lock} label="تأكيد كلمة المرور" type="password" value={form.confirm}
              onChange={set("confirm")} placeholder="أعد الكتابة" required testid="register-confirm-input" />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="register-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="register-submit-button"
            className="w-full bg-[#004EEB] hover:bg-[#0040C1] disabled:opacity-60 text-white font-semibold py-3.5 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#004EEB]/20 hover:shadow-[#004EEB]/30 hover:-translate-y-0.5"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            <span>{busy ? "جارٍ الإنشاء…" : "إنشاء الحساب"}</span>
            {!busy && <ArrowLeft className="w-4 h-4 rtl-flip" />}
          </button>

          <p className="text-center text-sm text-slate-500">
            لديك حساب؟{" "}
            <Link to="/login" className="text-[#004EEB] font-semibold hover:underline">
              سجّل الدخول
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}

function InputField({ icon: Icon, label, type, value, onChange, placeholder, required, testid }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-[11px] tracking-[0.1em] uppercase text-slate-500 font-semibold">{label}</span>
      <div className="relative">
        <Icon className="absolute start-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          data-testid={testid}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          className="w-full bg-white border border-slate-200 ps-10 pe-4 py-3 rounded-xl text-sm focus:outline-none focus:border-[#004EEB] focus:ring-2 focus:ring-[#004EEB]/10 transition-all text-right placeholder:text-slate-300 shadow-sm"
        />
      </div>
    </label>
  );
}
